import { useEffect, useRef } from "react";

import { constants, useBreakpointRange } from "@newsela/angelou";
import PropTypes from "prop-types";
import useGetExportables from "@/assignments/useGetExportables";
import {
  handleTitleChange,
  handleHasUnsavedChanges,
  handleSetAssignment,
} from "@/assignments/utils/assignmentActions";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import getIsAssignmentBuilderFormDirty from "@/assignments/utils/getIsAssignmentBuilderFormDirty";
import useSetAssignmentOnLoad from "@/assignments/utils/useSetAssignmentOnLoad";
import ActivityHeader from "@/view/ActivityHeader";
import AssignmentInstructionsInput from "@/view/ActivityPanel/AssignmentInstructionsInput";
import AssignmentTitleInput from "@/view/ActivityPanel/AssignmentTitleInput";
import AssignAction from "@/view/AssignAction";
import OmittedQuizMessage from "@/view/OmittedQuizMessage";
import PanelHeader from "@/view/PanelHeader";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";
import ExportablesModal from "@/components/AIComponents/ExportablesModal";
import {
  quizOmissionContainerCss,
  sectionCss,
  mobileSaveButtonCss,
} from "./TeacherActivityContainer.style";
import FormativeItemsContainer from "./FormativeItemsContainer";
import ActivitiesListContainer from "./ActivitiesListContainer";

const propTypes = {
  activeELALicense: PropTypes.bool,
  assignments: PropTypes.array,
  assignmentsLoading: PropTypes.bool,
  attachments: PropTypes.arrayOf(PropTypes.object),
  attachmentsGroupedBySubject: PropTypes.object,
  canSeeExportables: PropTypes.bool,
  closeButtonEventProperties: PropTypes.object,
  content: PropTypes.object,
  contentId: PropTypes.string,
  currentLevel: PropTypes.object,
  dispatch: PropTypes.func,
  fetchAssignment: PropTypes.func,
  formativeJwtResponse: PropTypes.object,
  latestAssignment: PropTypes.object,
  isSelectedAssignmentLoading: PropTypes.bool,
  language: PropTypes.string,
  legacyArticleLevelHeaderId: PropTypes.number,
  modalTriggerRef: PropTypes.object,
  onClickCloseButton: PropTypes.func,
  onModalOpen: PropTypes.func,
  quizOmissionReason: PropTypes.string,
  router: PropTypes.object,
  selectedAssignment: PropTypes.object,
  setVisibleActivityIds: PropTypes.func,
  shouldDisplayFormative: PropTypes.bool,
  shouldDisplayFormativeTOS: PropTypes.bool,
  shouldFocusCloseButtonRef: PropTypes.object,
  updatedAssignment: PropTypes.object,
  userProperties: PropTypes.object,
  visibleActivityIds: PropTypes.array,
};

const TeacherActivityContainer = ({
  activeELALicense,
  assignments,
  assignmentsLoading,
  attachments,
  attachmentsGroupedBySubject,
  canSeeExportables,
  closeButtonEventProperties,
  content,
  contentId,
  currentLevel,
  dispatch,
  fetchAssignment,
  formativeJwtResponse,
  isSelectedAssignmentLoading,
  latestAssignment,
  legacyArticleLevelHeaderId,
  modalTriggerRef,
  onClickCloseButton,
  onModalOpen,
  quizOmissionReason,
  router,
  setVisibleActivityIds,
  selectedAssignment,
  shouldDisplayFormative,
  shouldDisplayFormativeTOS,
  shouldFocusCloseButtonRef,
  updatedAssignment,
  userProperties,
  visibleActivityIds,
}) => {
  const { selectedAssignmentId } = router.query;

  const levelId = currentLevel?.id;
  const { data: exportablesMetadata, loading: exportablesLoading } =
    useGetExportables({
      articleId: contentId,
      levelId,
      skip: !canSeeExportables,
    });

  const isSavedAssignment = getIsSavedAssignment(selectedAssignmentId);

  // Form unsaved state:
  const initialAssignmentRef = useRef(null);
  const handleSetInitialAssignmentRef = (initialUpdatedAssignmentOnMount) => {
    initialAssignmentRef.current = initialUpdatedAssignmentOnMount;
  };

  // Determine if we should show mobile "Save changes" button
  const assignmentHasContentId = !!updatedAssignment?.content_id;
  const {
    breakpoints: {
      BreakpointRanges: { zeroToXs, xsToSm, smToMd },
    },
  } = constants;

  const breakpointRange = useBreakpointRange();
  const isMobile = [zeroToXs, xsToSm, smToMd].includes(breakpointRange);
  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isUpdateMode = assignmentMode === ASSIGNMENT_MODE.UPDATE;
  const showMobileSaveButton =
    assignmentHasContentId && isMobile && isUpdateMode;

  useSetAssignmentOnLoad({
    fetchAssignment,
    handleSetAssignment: (formattedAssignment) =>
      handleSetAssignment(formattedAssignment, dispatch),
    isSelectedAssignmentLoading,
    isStudent: false,
    latestAssignment,
    selectedAssignment,
  });

  useEffect(() => {
    if (initialAssignmentRef?.current && !updatedAssignment.hasUnsavedChanges) {
      /**
       * If the form values were previously dirty, then skip setting hasUnsavedChanges
       * to maintain dirty state until the form is saved.
       */
      const isDirty = getIsAssignmentBuilderFormDirty(
        initialAssignmentRef.current,
        updatedAssignment,
      );
      handleHasUnsavedChanges(isDirty, dispatch);
    }
  }, [updatedAssignment.hasUnsavedChanges]);

  return (
    <>
      <PanelHeader
        onClickCloseButton={onClickCloseButton}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <ActivityHeader isStudent={false} showOnboardingAssignmentBuilder />
      </PanelHeader>
      <div css={sectionCss}>
        <AssignmentTitleInput
          currentLevel={currentLevel}
          dispatch={dispatch}
          isSavedAssignment={isSavedAssignment}
          handleSetInitialAssignmentRef={handleSetInitialAssignmentRef}
          handleTitleChange={handleTitleChange}
          updatedAssignment={updatedAssignment}
          isLoading={isSelectedAssignmentLoading}
        />
        <AssignmentInstructionsInput />
      </div>
      {quizOmissionReason && (
        <div css={quizOmissionContainerCss}>
          <OmittedQuizMessage quizOmissionReason={quizOmissionReason} />
        </div>
      )}

      <div css={sectionCss}>
        <ActivitiesListContainer
          assignments={assignments}
          assignmentsLoading={assignmentsLoading}
          attachments={attachmentsGroupedBySubject}
          contentId={contentId}
          dispatch={dispatch}
          legacyArticleLevelHeaderId={legacyArticleLevelHeaderId}
          levelId={levelId}
          router={router}
          setVisibleActivityIds={setVisibleActivityIds}
          updatedAssignment={updatedAssignment}
          userProperties={userProperties}
          visibleActivityIds={visibleActivityIds}
        />
      </div>
      {canSeeExportables && (
        <div css={sectionCss}>
          <ExportablesModal
            exportablesMetadata={exportablesMetadata}
            exportablesLoading={exportablesLoading}
            articleShortTitle={currentLevel?.short_title}
          />
        </div>
      )}
      {shouldDisplayFormative && (
        <div css={sectionCss}>
          <FormativeItemsContainer
            activeELALicense={activeELALicense}
            attachments={attachments}
            contentId={contentId}
            formativeJwtResponse={formativeJwtResponse}
            modalTriggerRef={modalTriggerRef}
            onModalOpen={onModalOpen}
            shouldDisplayFormativeTOS={shouldDisplayFormativeTOS}
            userProperties={userProperties}
          />
        </div>
      )}
      {showMobileSaveButton && (
        <div css={[sectionCss, mobileSaveButtonCss]}>
          <AssignAction
            content={content}
            currentLevelId={levelId}
            isMobile={isMobile}
            originalAssignment={updatedAssignment}
            userProperties={userProperties}
            showMobileButton
          />
        </div>
      )}
    </>
  );
};

TeacherActivityContainer.propTypes = propTypes;

export default TeacherActivityContainer;
